import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'

import useAuth from '../../hooks/useAuth'
import styled from 'styled-components'
import Heading from '../Heading/Heading'
import Button from '../Button/Button'
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal"
import createNewUser from '../../graphql/createNewUser'
import getUsers from '../../graphql/getUsers'
import { validate as validateEmail } from 'isemail'
import { parseOneAddress as parseEmail } from 'email-addresses'

export const btnStyles = { marginLeft: '10px' }
export const Wrapper = styled.div`
  padding-right: 45px;
  margin-bottom: 44px;

  @media only screen and (max-width: 920px) {
    padding: 0 18px;
  }
`

export const Col = styled.div`
  display: flex;
  flex-direction: column;
`

export const Row = styled.div`
  display: flex;
  align-items: center;

  & > label {
    font-size: 14px;
    color: var(--color-text-primary);
    font-family: 'Roboto', Arial, sans-serif;
    font-weight: 500;
    margin-left: 8px;
    margin-top: -2px;
  }
`

export const Box = styled.div`
  margin-top: 35px;
  margin-bottom: 20px;
  padding: 24px 20px;
  border-radius: 8px;
  background-color: var(--color-bg-card);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 20px;

  @media only screen and (max-width: 450px) {
    grid-template-columns: 1fr;
    padding: 20px 16px;
  }
`

export const Input = styled.input`
  border: 2px solid var(--color-text-secondary);
  background-color: var(--color-bg-input);
  padding: 12px 16px;
  border-radius: 8px;
  color: var(--color-input-text, #4b5882);
  font-size: 14px;
  font-family: 'Roboto', Arial, sans-serif;
  font-weight: 500;
  transition: all 0.2s ease;

  &::placeholder {
    color: var(--color-text-secondary);
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(151, 202, 111, 0.2);
    background-color: #fff;
  }

  &:hover {
    border-color: var(--color-primary);
  }

  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--color-text-disabled);
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export const Label = styled.span`
  font-size: 14px;
  font-weight: 600;
  font-family: 'Roboto', Arial, sans-serif;
  margin-bottom: 8px;
  color: var(--color-text-primary);
  display: block;
`

export const ErrorMsg = styled.span`
  color: var(--color-error);
  font-size: 12px;
  font-family: 'Roboto', Arial, sans-serif;
  font-weight: 500;
  margin: 8px 0 0 0;
  display: block;
`

const NewUser = ({ domainWhitelist = [] }) => {
  const history = useHistory()
  const { isSuperAdmin, isOrgAdmin, selectedOrg } = useAuth()
  const [createUser, { loading, error }] = useMutation(createNewUser)

  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    roles: ['DOCUMENTATION', 'SUPPORT', 'MONITOR'],
  })
  const [errors, setErrors] = useState({})

  const [isConfirmationOpen, setConfirmationOpen] = useState(false)
  const toggleConfirmation = () => setConfirmationOpen(open => !open)

  const restrictedTo = [isSuperAdmin, isOrgAdmin]
  if (!restrictedTo.includes(true)) history.push('/users')

  const { local = "", domain = "" } = parseEmail(form.email) ?? {}

  const handleChange = e => {
    const { name, value, checked } = e.target

    const newValues = {
      ...(name.includes('roles')
        ? {
          [name]: checked
            ? [...form.roles, value]
            : [...form.roles].filter(role => role !== value),
        }
        : { [name]: value }),
    }

    setForm(prev => ({ ...prev, ...newValues }))
  }

  const handleSubmit = () => {
    const errors = validate(form)

    if (errors) return setErrors(errors)
    setErrors({})

    if (!isWhitelisted(domain, domainWhitelist)) return toggleConfirmation()

    return inviteUser()
  }

  const inviteUser = async () => {
    try {
      setSubmitting(true)

      const variables = {
        organizationId: selectedOrg.value,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email.trim(),
        roles: form.roles,
      }

      await createUser({
        variables,
        refetchQueries: [
          {
            query: getUsers,
            variables: { id: selectedOrg.value }
          },
        ],
      })

      setSubmitting(false)
      history.push('/users')
    } catch (err) {
      console.log(err)
    }
  }

  if (error) return <ErrorMsg>{error.message}</ErrorMsg>

  return (
    <Wrapper>
      <Heading>Invite User</Heading>
      <Box>
        <Col>
          <Label as="label" htmlFor="firstName">First Name</Label>
          <Input
            id='firstName'
            name='firstName'
            value={form.firstName}
            onChange={handleChange}
            aria-invalid={!!errors.firstName}
            aria-describedby={errors.firstName ? 'firstName-error' : undefined}
          />
          {errors.firstName && <ErrorMsg id="firstName-error" role="alert">{errors.firstName}</ErrorMsg>}
        </Col>
        <Col>
          <Label as="label" htmlFor="lastName">Last Name</Label>
          <Input
            id='lastName'
            name='lastName'
            value={form.lastName}
            onChange={handleChange}
            aria-invalid={!!errors.lastName}
            aria-describedby={errors.lastName ? 'lastName-error' : undefined}
          />
          {errors.lastName && <ErrorMsg id="lastName-error" role="alert">{errors.lastName}</ErrorMsg>}
        </Col>
        <Col>
          <Label as="label" htmlFor="email">Email</Label>
          <Input
            id='email'
            name='email'
            type='email'
            value={form.email}
            onChange={handleChange}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {errors.email && <ErrorMsg id="email-error" role="alert">{errors.email}</ErrorMsg>}
        </Col>
        <Col>
          <Label id="roles-label">Roles</Label>
          <Row>
            <input
              type='checkbox'
              id='roles'
              name='roles'
              value={'admin'.toUpperCase()}
              onChange={handleChange}
              aria-labelledby="roles-label"
            />
            <label htmlFor='roles'>Admin</label>
          </Row>
        </Col>
      </Box>
      <Row>
        <Button isSecondary onClick={() => history.push('/users')}>
          Cancel
        </Button>
        <Button disabled={loading} isSubmitting={submitting} onClick={handleSubmit} style={btnStyles}>
          Save
        </Button>
      </Row>
      {isConfirmationOpen && (
        <ConfirmationModal
          isOpen={isConfirmationOpen}
          toggle={toggleConfirmation}
          title='Email Outside Domain'
          message={`
            <p>${local}@<strong>${domain}</strong> is not a whitelisted domain for <strong>${selectedOrg.label}</strong></p>
            <br />
            <p>Are you sure you want to add <strong>${form.firstName} ${form.lastName}</strong>?</p>
            <br />
            <p><strong>Whitelisted Domains</strong>
            <br />
            ${domainWhitelist.join(", ")}</p>
          `}
          handler={inviteUser}
          submitting={submitting}
        />
      )}
    </Wrapper>
  )
}

const validatePhone = (phoneNumber) => {
  return !!phoneNumber.match(/^\d{3}-\d{3}-\d{4}$/g);
}

export function validate(obj) {
  const errors = {}
  const whenCapitalized = /(?=[A-Z])/
  const transform = key => key.split(whenCapitalized).join(' ').toLowerCase()

  for (const [key, value] of Object.entries(obj)) {
    if ((value === "" || value === undefined || value === null) && key !== "phone") errors[key] = `Please fill ${transform(key)} field!`
    if (key === "email" && value && !validateEmail(value)) errors[key] = 'Please provide a valid email'
    if (key === "phone" && value && !validatePhone(value)) errors[key] = 'Please provide a valid phone number'
  }

  return !!Object.keys(errors).length ? errors : null
}

function isWhitelisted(candidateDomain, domainWhitelist = []) {
  return domainWhitelist.includes(candidateDomain)
}

export default NewUser
